import { Workbook } from "exceljs";
import {
	reportsService,
	EnrollmentSummaryResponse,
	IncentiveDisbursementResponse,
	EducationalEngagementResponse,
	EventActivityResponse,
	EventRatingsResponse,
} from "../../services/reports.service";
import { addCoverSheet } from "./cover-sheet";
import { addEnrollmentSummarySheet } from "./enrollment-summary-sheet";
import { addIncentiveDisbursementSheets } from "./incentive-disbursement-sheets";
import { addEducationalEngagementSheets } from "./educational-engagement-sheets";
import { addEventActivitySheet } from "./event-activity-sheet";
import { addEventRatingsSheets } from "./event-ratings-sheets";
import { filenameSlug, isoDateOnly } from "./workbook-styles";

export interface ProgramReportConfig {
	incentiveDisbursement?: boolean;
	educationalEngagement?: boolean;
	eventActivity?: boolean;
	eventRatings?: boolean;
}

export interface BuildWorkbookFilters {
	accountId: string;
	programId: string;
	programName: string;
	reportConfig: ProgramReportConfig;
	startDate?: string;
	endDate?: string;
	orgUnitId?: string;
	researcherId?: string;
	eventId?: string;
	enrollmentId?: string;
}

export interface BuiltWorkbook {
	blob: Blob;
	filename: string;
}

// Backend caps pageSize at 100 (@Max(100) on the DTO). Export needs the full
// dataset, so we page through the entire result set client-side. KPIs and
// other aggregates are the same on every page — only the per-enrollment row
// list is concatenated.
const EXPORT_PAGE_SIZE = 100;

const fetchAllPages = async <
	TResp extends { pagination?: { total: number; pageSize: number } },
>(
	fetchPage: (pageNumber: number) => Promise<TResp>,
	rowsKey: keyof TResp,
): Promise<TResp> => {
	const first = await fetchPage(0);
	const pagination = first.pagination;
	if (!pagination || pagination.total <= pagination.pageSize) {
		return first;
	}
	const totalPages = Math.ceil(pagination.total / pagination.pageSize);
	const rest = await Promise.all(
		Array.from({ length: totalPages - 1 }, (_, i) => fetchPage(i + 1)),
	);
	const firstRows = first[rowsKey] as unknown as unknown[];
	const merged = [
		...firstRows,
		...rest.flatMap((p) => p[rowsKey] as unknown as unknown[]),
	];
	return { ...first, [rowsKey]: merged };
};

export const buildWorkbook = async (
	filters: BuildWorkbookFilters,
): Promise<BuiltWorkbook> => {
	const baseFilters = {
		accountId: filters.accountId,
		programId: filters.programId,
		startDate: filters.startDate,
		endDate: filters.endDate,
		orgUnitId: filters.orgUnitId,
		researcherId: filters.researcherId,
	};

	const enrollmentSummaryPromise = fetchAllPages<EnrollmentSummaryResponse>(
		(pageNumber) =>
			reportsService.getEnrollmentSummary({
				...baseFilters,
				pageNumber,
				pageSize: EXPORT_PAGE_SIZE,
			}),
		"enrollments",
	);

	const incentivePromise = filters.reportConfig.incentiveDisbursement
		? fetchAllPages<IncentiveDisbursementResponse>(
				(pageNumber) =>
					reportsService.getIncentiveDisbursement({
						...baseFilters,
						pageNumber,
						pageSize: EXPORT_PAGE_SIZE,
					}),
				"byEnrollment",
			)
		: Promise.resolve(null);

	const educationalPromise = filters.reportConfig.educationalEngagement
		? fetchAllPages<EducationalEngagementResponse>(
				(pageNumber) =>
					reportsService.getEducationalEngagement({
						accountId: filters.accountId,
						programId: filters.programId,
						startDate: filters.startDate,
						endDate: filters.endDate,
						pageNumber,
						pageSize: EXPORT_PAGE_SIZE,
					}),
				"byEnrollment",
			)
		: Promise.resolve(null);

	const eventActivityPromise = filters.reportConfig.eventActivity
		? fetchAllPages<EventActivityResponse>(
				(pageNumber) =>
					reportsService.getEventActivity({
						accountId: filters.accountId,
						programId: filters.programId,
						startDate: filters.startDate,
						endDate: filters.endDate,
						pageNumber,
						pageSize: EXPORT_PAGE_SIZE,
					}),
				"rows",
			)
		: Promise.resolve(null);

	// Event ratings: scoped to the event currently selected in the EventRatings
	// tab. Spec: SOWS → 2 sheets; Mood/Sleep (independent ratings) → 3 sheets.
	// If no event is selected, the backend auto-picks the first rateable event
	// for the program (matches the tab's own auto-pick behavior).
	const eventRatingsPromise = filters.reportConfig.eventRatings
		? (reportsService.getEventRatings({
				accountId: filters.accountId,
				programId: filters.programId,
				eventId: filters.eventId,
				enrollmentId: filters.enrollmentId,
				startDate: filters.startDate,
				endDate: filters.endDate,
			}) as Promise<EventRatingsResponse>)
		: Promise.resolve(null);

	const [
		enrollmentSummary,
		incentive,
		educational,
		eventActivity,
		eventRatings,
	] = await Promise.all([
		enrollmentSummaryPromise,
		incentivePromise,
		educationalPromise,
		eventActivityPromise,
		eventRatingsPromise,
	]);

	const workbook = new Workbook();
	workbook.creator = "SIMS Reporting";
	workbook.created = new Date();

	addCoverSheet(workbook, {
		programName: filters.programName,
		exportedAt: new Date(),
		startDate: filters.startDate,
		endDate: filters.endDate,
		orgUnitFilter: filters.orgUnitId,
		researcherFilter: filters.researcherId,
	});

	addEnrollmentSummarySheet(workbook, enrollmentSummary);
	if (incentive) addIncentiveDisbursementSheets(workbook, incentive);
	if (educational) addEducationalEngagementSheets(workbook, educational);
	if (eventActivity) addEventActivitySheet(workbook, eventActivity);
	if (eventRatings) addEventRatingsSheets(workbook, eventRatings);

	const arrayBuffer = await workbook.xlsx.writeBuffer();
	const blob = new Blob([arrayBuffer], {
		type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
	});

	const filename = `consolidated_${filenameSlug(filters.programName)}_${isoDateOnly(filters.startDate)}_${isoDateOnly(filters.endDate)}.xlsx`;

	return { blob, filename };
};

export const triggerDownload = ({ blob, filename }: BuiltWorkbook): void => {
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = filename;
	document.body.appendChild(a);
	a.click();
	a.remove();
	URL.revokeObjectURL(url);
};
